import jsPDF from "jspdf";
import { RefObject } from "react";

import { downloadFile } from "@nens/shared-utils";

import { DEFAULT_MEDIA_OPTIONS } from "./utils/default-media-options";
import { A4_LONG_SIDE, A4_SHORT_SIDE, HEIGHT_MARGIN, WIDTH_MARGIN } from "./utils/pdf-consts";

export interface ScreenshotProps {
  screenshotWrapperRef: RefObject<HTMLDivElement>;
  fileName?: string;
  pdf?: boolean;
  orientation?: "portrait" | "landscape"; // Allow user to specify orientation
}

interface SavePdfFileProps extends Pick<ScreenshotProps, "orientation" | "fileName"> {
  canvas: HTMLCanvasElement;
}

const savePdfFile = ({ orientation, canvas, fileName }: SavePdfFileProps) => {
  const isLandscape = orientation === "landscape";
  const pageWidth = isLandscape ? A4_LONG_SIDE : A4_SHORT_SIDE;
  const pageHeight = isLandscape ? A4_SHORT_SIDE : A4_LONG_SIDE;

  const pdf = new jsPDF(isLandscape ? "l" : "p", "pt", [pageWidth, pageHeight]);

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  const imgWidth = pageWidth - 2 * WIDTH_MARGIN; // max width in PDF
  const imgHeight = (imgWidth / canvasWidth) * canvasHeight; // scaled height

  const pageImg = canvas.toDataURL("image/png", 1.0);

  // Add the image to the PDF
  pdf.addImage(
    pageImg,
    "PNG",
    WIDTH_MARGIN, // x
    HEIGHT_MARGIN, // y
    imgWidth, // scaled width
    imgHeight // scaled height
  );

  // Save the PDF
  pdf.save(fileName);
};

export function useScreenshot({
  screenshotWrapperRef,
  pdf,
  fileName = pdf ? "screenshot.pdf" : "screenshot.png",
  orientation = "landscape",
}: ScreenshotProps) {
  return async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!screenshotWrapperRef.current) return;
    const screenshotWrapper = screenshotWrapperRef.current;
    const video = document.createElement("video");

    try {
      const stream: MediaStream = await navigator.mediaDevices.getDisplayMedia(
        DEFAULT_MEDIA_OPTIONS
      );
      video.srcObject = stream;
      await video.play();
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = screenshotWrapper.clientWidth * pixelRatio;
      canvas.height = screenshotWrapper.clientHeight * pixelRatio;

      // Scale the context to ensure crisp rendering
      context?.scale(pixelRatio, pixelRatio);

      // Set the CSS width and height for visual alignment
      canvas.style.width = `${screenshotWrapper.clientWidth}px`;
      canvas.style.height = `${screenshotWrapper.clientHeight}px`;
      const clientRect = screenshotWrapper.getBoundingClientRect();

      // Draw only the part inside the screenshotWrapper to the canvas
      context?.drawImage(
        video,
        (video.videoWidth * clientRect.left) / window.innerWidth,
        (video.videoHeight * clientRect.top) / window.innerHeight,
        (video.videoWidth * screenshotWrapper.clientWidth) / window.innerWidth,
        (video.videoHeight * screenshotWrapper.clientHeight) / window.innerHeight,
        0,
        0,
        screenshotWrapper.clientWidth,
        screenshotWrapper.clientHeight
      );

      if (pdf) {
        savePdfFile({ fileName, orientation, canvas });
      } else {
        const frame = canvas.toDataURL();
        downloadFile(frame, fileName);
      }

      stream.getTracks().forEach((track) => track.stop());
      video.pause();
    } catch (err) {
      console.error("Error: " + err);
    }
  };
}
